import { fetchAdminSettings, patchAdminSettings, type IAdminSettings } from '@/api/admin';
import { uploadImage } from '@/api/upload-image';
import IconGrid, { IconGridSection } from '@/components/build/icon-grid';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { $updateFeatures } from '@/entities/features-entity';
import { useUnit } from 'effector-react';
import { ContainerIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { toast } from 'sonner';

const MIN_LIMIT = 1;
const MAX_LIMIT = 100;
const MAX_DEFAULT_ICONS = 50;

export default function BuilderSettingsWidget() {
	const updateFeatures = useUnit($updateFeatures);
	const fileRef = useRef<HTMLInputElement | null>(null);
	const [builderEnabled, setBuilderEnabled] = useState(true);
	const [dailyBuildLimitInput, setDailyBuildLimitInput] = useState('');
	const [defaultBuilderIcons, setDefaultBuilderIcons] = useState<string[]>([]);
	const [encryptEnabled, setEncryptEnabled] = useState(true);
	const [encryptDexloader, setEncryptDexloader] = useState<'yes' | 'no'>('no');
	const [encryptAssets, setEncryptAssets] = useState<'yes' | 'no'>('no');
	const [encryptRenamePackage, setEncryptRenamePackage] = useState<'yes' | 'no'>('no');
	const [testBuildEnabled, setTestBuildEnabled] = useState(false);
	const [builderAndroidProjects, setBuilderAndroidProjects] = useState<IAdminSettings['builderAndroidProjects']>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isUploadingIcon, setIsUploadingIcon] = useState(false);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) {
					setBuilderEnabled(settings.builderEnabled);
					setDailyBuildLimitInput(String(settings.dailyBuildLimit));
					setDefaultBuilderIcons(settings.defaultBuilderIcons ?? []);
					setEncryptEnabled(settings.encryptEnabled ?? true);
					setEncryptDexloader(settings.encryptDexloader ?? 'no');
					setEncryptAssets(settings.encryptAssets ?? 'no');
					setEncryptRenamePackage(settings.encryptRenamePackage ?? 'no');
					setTestBuildEnabled(settings.testBuildEnabled ?? false);
					setBuilderAndroidProjects(settings.builderAndroidProjects ?? []);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	async function handleToggle(checked: boolean) {
		const previous = builderEnabled;
		setBuilderEnabled(checked);
		updateFeatures({ builderEnabled: checked });
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ builderEnabled: checked });
			setBuilderEnabled(settings.builderEnabled);
			updateFeatures({ builderEnabled: settings.builderEnabled });
			toast.success(checked ? 'Билдер включён' : 'Билдер отключён');
		} catch {
			setBuilderEnabled(previous);
			updateFeatures({ builderEnabled: previous });
			toast.error('Не удалось сохранить настройку');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleLimitSubmit(e: FormEvent) {
		e.preventDefault();

		const dailyBuildLimit = Number(dailyBuildLimitInput);
		if (
			!dailyBuildLimitInput.trim() ||
			!Number.isInteger(dailyBuildLimit) ||
			dailyBuildLimit < MIN_LIMIT ||
			dailyBuildLimit > MAX_LIMIT
		) {
			toast.error(`Лимит должен быть от ${MIN_LIMIT} до ${MAX_LIMIT}`);
			return;
		}

		const previous = dailyBuildLimitInput;
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ dailyBuildLimit });
			setDailyBuildLimitInput(String(settings.dailyBuildLimit));
			toast.success('Лимит сборок сохранён');
		} catch {
			setDailyBuildLimitInput(previous);
			toast.error('Не удалось сохранить настройку');
		} finally {
			setIsSaving(false);
		}
	}

	async function saveDefaultIcons(nextIcons: string[]) {
		const previous = defaultBuilderIcons;
		setDefaultBuilderIcons(nextIcons);
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ defaultBuilderIcons: nextIcons });
			setDefaultBuilderIcons(settings.defaultBuilderIcons ?? []);
			toast.success('Иконки по умолчанию сохранены');
		} catch {
			setDefaultBuilderIcons(previous);
			toast.error('Не удалось сохранить иконки');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleTestBuildToggle(checked: boolean) {
		const previous = testBuildEnabled;
		setTestBuildEnabled(checked);
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ testBuildEnabled: checked });
			setTestBuildEnabled(settings.testBuildEnabled);
			toast.success(checked ? 'Тестовый билд включён для новых сборок' : 'Прод билд: логи в APK отключены');
		} catch {
			setTestBuildEnabled(previous);
			toast.error('Не удалось сохранить настройку');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleEncryptSettingsSave(patch: Partial<IAdminSettings>) {
		setIsSaving(true);
		try {
			const settings = await patchAdminSettings(patch);
			setEncryptEnabled(settings.encryptEnabled);
			setEncryptDexloader(settings.encryptDexloader);
			setEncryptAssets(settings.encryptAssets);
			setEncryptRenamePackage(settings.encryptRenamePackage);
			toast.success('Настройки шифрования сохранены');
		} catch {
			toast.error('Не удалось сохранить настройки шифрования');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleIconUpload(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		if (defaultBuilderIcons.length >= MAX_DEFAULT_ICONS) {
			toast.error(`Максимум ${MAX_DEFAULT_ICONS} иконок`);
			e.target.value = '';
			return;
		}

		setIsUploadingIcon(true);

		try {
			const url = await uploadImage(file, file.name);
			if (defaultBuilderIcons.includes(url)) {
				toast.error('Эта иконка уже добавлена');
				return;
			}
			await saveDefaultIcons([...defaultBuilderIcons, url]);
		} catch {
			toast.error('Не удалось загрузить иконку');
		} finally {
			setIsUploadingIcon(false);
			e.target.value = '';
		}
	}

	async function handleIconRemove(url: string) {
		if (!window.confirm('Удалить иконку из списка по умолчанию?')) {
			return;
		}

		await saveDefaultIcons(defaultBuilderIcons.filter(icon => icon !== url));
	}

	return (
		<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
			<CardHeader className='border-b px-4 py-4 flex'>
				<div className='flex items-center gap-3'>
					<div className='rounded-lg bg-chart-4/15 p-2'>
						<ContainerIcon className='text-chart-4' size={20} />
					</div>
					<div>
						<CardTitle className='text-chart-4'>Билдер</CardTitle>
						<CardDescription>Сборка APK через панель</CardDescription>
					</div>
				</div>
			</CardHeader>

			<LoadingWrapper show={isLoading || isSaving || isUploadingIcon}>
				<CardContent className='px-4 py-4 flex flex-col gap-4'>
					<div className='flex items-center justify-between gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Доступен пользователям</Label>
							<span className='text-xs text-muted-foreground'>
								Блокирует создание новых сборок. Страница билдера и история остаются доступными
							</span>
						</div>
						<Switch checked={builderEnabled} onCheckedChange={handleToggle} disabled={isLoading} />
					</div>

					<Separator />

					<form className='flex flex-col gap-3' onSubmit={handleLimitSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='daily-build-limit'>Билдов в день</Label>
							<span className='text-xs text-muted-foreground'>
								Считаются только успешно собранные APK за календарные сутки (UTC)
							</span>
							<InputGroup>
								<InputGroupInput
									id='daily-build-limit'
									className='appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
									type='number'
									inputMode='numeric'
									min={MIN_LIMIT}
									max={MAX_LIMIT}
									value={dailyBuildLimitInput}
									onChange={e => setDailyBuildLimitInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>
						<Button type='submit' className='self-start' disabled={isLoading || isSaving}>
							Сохранить
						</Button>
					</form>

					<Separator />

					<div className='flex flex-col gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Шифрование APK (smart-bin)</Label>
							<span className='text-xs text-muted-foreground'>
								Глобальные настройки post-processing для всех сборок
							</span>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label>Шифровать APK</Label>
								<span className='text-xs text-muted-foreground'>
									Выключите для raw-сборок без encrypt (бейдж TEST в истории)
								</span>
							</div>
							<Switch
								checked={encryptEnabled}
								onCheckedChange={checked => void handleEncryptSettingsSave({ encryptEnabled: checked })}
								disabled={isLoading || isSaving}
							/>
						</div>

						{encryptEnabled ? (
							<>
								<div className='flex items-center justify-between gap-3'>
									<div className='flex flex-col gap-0.5'>
										<Label>DexLoader</Label>
										<span className='text-xs text-muted-foreground'>
											Выключите, если launch падает на зашифрованном APK
										</span>
									</div>
									<Switch
										checked={encryptDexloader === 'yes'}
										onCheckedChange={checked =>
											void handleEncryptSettingsSave({ encryptDexloader: checked ? 'yes' : 'no' })
										}
										disabled={isLoading || isSaving}
									/>
								</div>

								<div className='flex items-center justify-between gap-3'>
									<div className='flex flex-col gap-0.5'>
										<Label>Шифровать assets</Label>
										<span className='text-xs text-muted-foreground'>
											Выключите, чтобы assets оставались открытыми
										</span>
									</div>
									<Switch
										checked={encryptAssets === 'yes'}
										onCheckedChange={checked =>
											void handleEncryptSettingsSave({ encryptAssets: checked ? 'yes' : 'no' })
										}
										disabled={isLoading || isSaving}
									/>
								</div>

								<div className='flex items-center justify-between gap-3'>
									<div className='flex flex-col gap-0.5'>
										<Label>Переименование пакета</Label>
										<span className='text-xs text-muted-foreground'>
											При включении new_package = applicationId билда
										</span>
									</div>
									<Switch
										checked={encryptRenamePackage === 'yes'}
										onCheckedChange={checked =>
											void handleEncryptSettingsSave({ encryptRenamePackage: checked ? 'yes' : 'no' })
										}
										disabled={isLoading || isSaving}
									/>
								</div>
							</>
						) : null}
					</div>

					<Separator />

					<div className='flex items-center justify-between gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Тестовый билд</Label>
							<span className='text-xs text-muted-foreground'>
								Включено — логи на экране и полное debug-логирование в APK. Выключено — прод: без логов
							</span>
						</div>
						<Switch
							checked={testBuildEnabled}
							onCheckedChange={checked => void handleTestBuildToggle(checked)}
							disabled={isLoading || isSaving}
						/>
					</div>

					<Separator />

					<div className='flex flex-col gap-2'>
						<div className='flex flex-col gap-0.5'>
							<Label>Android-проекты</Label>
							<span className='text-xs text-muted-foreground'>
								Доступные шаблоны для сборки APK (фиксированный список)
							</span>
						</div>
						<ul className='flex flex-col gap-1.5 text-sm'>
							{builderAndroidProjects.map(project => (
								<li key={project.id} className='rounded-md border border-border px-3 py-2'>
									<p className='font-medium'>{project.label}</p>
									<p className='text-xs text-muted-foreground'>{project.dir}</p>
								</li>
							))}
						</ul>
					</div>

					<Separator />

					<IconGridSection
						title='Иконки по умолчанию'
						count={defaultBuilderIcons.length}
						emptyText={
							defaultBuilderIcons.length === 0
								? 'Загрузите иконки, которые будут доступны всем пользователям при создании сборки'
								: undefined
						}
					>
						<IconGrid
							icons={defaultBuilderIcons}
							disabled={isLoading || isSaving || isUploadingIcon}
							showUpload={defaultBuilderIcons.length < MAX_DEFAULT_ICONS}
							showRemove
							onUploadClick={() => fileRef.current?.click()}
							onRemove={url => void handleIconRemove(url)}
						/>
					</IconGridSection>

					<input
						type='file'
						ref={fileRef}
						onChange={handleIconUpload}
						accept='image/*'
						className='sr-only'
						disabled={isLoading || isSaving || isUploadingIcon}
					/>
				</CardContent>
			</LoadingWrapper>
		</Card>
	);
}
